import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { m as motion } from "framer-motion";
import api from "../lib/api";
import SafeImage from "../components/SafeImage";

/**
 * Reusable trilogy / universe hub page.
 * SEO-compliant: unique title/description, single H1, canonical, OpenGraph, indexable.
 * Aggregates the tomes of a trilogy and links to individual novel pages.
 */
export default function TrilogyHub({
  slug,               // canonical slug of this hub (used for canonical URL)
  title,              // hub title (H1)
  subtitle,           // eyebrow / kicker
  metaTitle,          // <title>
  metaDescription,    // meta description
  intro,              // JSX or string — presentation paragraphs
  tomeSlugs,          // ordered array of novel slugs that compose the trilogy
  coverUrl,           // hero image for OG + top section
  otherNovels,        // ordered array of novel slugs for internal linking cross-universes
}) {
  const [novels, setNovels] = useState([]);

  useEffect(() => {
    api.get("/novels")
      .then((r) => setNovels(r.data || []))
      .catch(() => setNovels([]));
  }, []);

  const tomes = tomeSlugs
    .map((s) => novels.find((n) => n.slug === s))
    .filter(Boolean);
  const others = (otherNovels || [])
    .map((s) => novels.find((n) => n.slug === s))
    .filter(Boolean);

  // Malo trilogy tomes use /romans/{slug}/tome-{N}; others use /romans/{slug}
  const buildTomeUrl = (n) =>
    n.trilogy_key === "malo" && n.trilogy_tome
      ? `/romans/${n.slug}/tome-${n.trilogy_tome}`
      : `/romans/${n.slug}`;

  const canonical = `https://brunocourtin.fr/romans/${slug}`;
  const ogImage = coverUrl?.startsWith("http")
    ? coverUrl
    : `https://brunocourtin.fr${coverUrl || ""}`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="book" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <main className="pt-24 pb-24" data-testid={`trilogy-hub-${slug}`}>
        <div className="max-w-5xl mx-auto px-6">
          {/* Hero */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-10"
          >
            <p className="eyebrow mb-3" style={{ color: "var(--copper)" }}>{subtitle}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[var(--ink)] leading-tight">
              {title}
            </h1>
          </motion.div>

          {coverUrl && (
            <div className="mb-12 rounded-lg overflow-hidden shadow-2xl aspect-[4/3] max-w-3xl">
              <SafeImage src={coverUrl} alt={title} className="w-full h-full object-cover" />
            </div>
          )}

          {/* Intro / original text */}
          <div className="prose prose-lg max-w-3xl text-[var(--ink)] mb-16 font-body space-y-5 leading-relaxed">
            {intro}
          </div>

          {/* Tomes list */}
          <section className="mb-16">
            <h2 className="font-display text-2xl sm:text-3xl mb-6 text-[var(--ink)]">
              Les romans de cette trilogie
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {tomes.map((t) => (
                <Link
                  key={t.slug}
                  to={buildTomeUrl(t)}
                  data-testid={`hub-tome-link-${t.slug}`}
                  className="group border border-[var(--line)] p-4 hover:border-[var(--copper)] transition-colors"
                >
                  {t.cover_url && (
                    <div className="aspect-[2/3] mb-3 overflow-hidden">
                      <SafeImage src={t.cover_url} alt={t.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <p className="eyebrow" style={{ color: "var(--copper)" }}>
                    {t.status === "publie" ? "Disponible" : "À paraître"}
                  </p>
                  <h3 className="font-display text-lg text-[var(--ink)] mt-1">{t.title}</h3>
                  {t.subtitle && <p className="text-sm italic opacity-70 mt-1">{t.subtitle}</p>}
                </Link>
              ))}
            </div>
          </section>

          {/* Amazon buttons for available tomes only */}
          <section className="mb-16">
            <h2 className="font-display text-2xl sm:text-3xl mb-6 text-[var(--ink)]">
              Disponibilité
            </h2>
            <div className="space-y-3">
              {tomes.map((t) => {
                const amz = (t.purchase_links || []).find(
                  (l) => l.store === "amazon" && l.url
                );
                return (
                  <div key={t.slug} className="flex items-center gap-4 border-b border-[var(--line)] pb-3">
                    <span className="flex-1 font-display text-lg text-[var(--ink)]">{t.title}</span>
                    {amz ? (
                      <a
                        href={amz.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`hub-amazon-${t.slug}`}
                        className="btn-editorial filled"
                      >
                        Acheter sur Amazon →
                      </a>
                    ) : (
                      <span
                        className="text-sm font-mono opacity-70"
                        data-testid={`hub-a-paraitre-${t.slug}`}
                      >
                        Roman à paraître
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Cross links */}
          <section className="mb-16">
            <h2 className="font-display text-2xl sm:text-3xl mb-6 text-[var(--ink)]">
              Découvrez les autres romans de Bruno Courtin
            </h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 list-none">
              {others.map((n) => (
                <li key={n.slug}>
                  <Link
                    to={buildTomeUrl(n)}
                    data-testid={`hub-other-link-${n.slug}`}
                    className="block px-4 py-3 border border-[var(--line)] hover:border-[var(--copper)] transition-colors text-[var(--ink)]"
                  >
                    <span className="font-display">{n.title}</span>
                    {n.subtitle && (
                      <span className="text-sm italic opacity-70"> — {n.subtitle}</span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          {/* Author link */}
          <section className="border-t border-[var(--line)] pt-8">
            <p className="text-sm opacity-80">
              Cette trilogie est écrite par{" "}
              <Link
                to="/auteur"
                data-testid="hub-author-link"
                className="underline decoration-[var(--copper)] hover:text-[var(--copper)] transition-colors"
              >
                Bruno Courtin
              </Link>
              , auteur français de romans historiques et d&apos;anticipation.
            </p>
          </section>
        </div>
      </main>
    </>
  );
}
